/**
 * Frontend-API für die Playbook-Engine (compass-new, Migration 117 · FR19/FR20/FR21).
 * Wrapper um `/api/playbooks`: CRUD der Definitionen (Editor, Story 11), Run-Historie +
 * Live-Status + Dry-Run-Auslösung (Observability, Story 12). Muster wie `signal-sources-api.ts`.
 */

/**
 * Ein Baustein der linearen Kette. Linearität via `next`; Verzweigung NUR beim
 * `condition`-Baustein via `nextTrue`/`nextFalse`. `next: null` = Kette zu Ende.
 * Spiegelt `PlaybookBlock` aus dem Backend (db/repositories/playbook-definition-repository.ts).
 */
export interface PlaybookBlock {
  id: string;
  type: string;
  next?: string | null;
  nextTrue?: string | null;
  nextFalse?: string | null;
  config?: Record<string, unknown>;
}

export interface PlaybookDefinition {
  id: number;
  companyId: number;
  goalNodeId: number;
  name: string;
  enabled: boolean;
  blocks: PlaybookBlock[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlaybookInput {
  companyId?: number;
  goalNodeId: number;
  name: string;
  enabled?: boolean;
  blocks?: PlaybookBlock[];
}

export interface UpdatePlaybookFields {
  name?: string;
  enabled?: boolean;
  blocks?: PlaybookBlock[];
}

export type PlaybookRunStatus =
  | 'running'
  | 'awaiting_delay'
  | 'awaiting_approval'
  | 'done'
  | 'failed'
  | 'stopped';

export interface PlaybookRun {
  id: string;
  playbookId: number;
  companyId: number;
  originSignalId: string | null;
  status: PlaybookRunStatus;
  currentBlockId: string | null;
  context: Record<string, unknown>;
  definitionSnapshot: PlaybookBlock[];
  resumeAt: string | null;
  pendingActionId: string | null;
  dryRun: boolean;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Ein `playbook.*`-Event aus dem Run-Stream (Run-Historie, FR19). */
export interface PlaybookEvent {
  id?: number;
  streamId?: string;
  eventType: string;
  payload: Record<string, unknown>;
  /** Zeitstempel des Events (`StoredEvent.occurredAt`); `createdAt` als Fallback für Altform. */
  occurredAt?: string;
  createdAt?: string;
  [k: string]: unknown;
}

export interface RunWithEvents {
  run: PlaybookRun;
  events: PlaybookEvent[];
}

const URL = '/api/playbooks';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const b = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(b.error?.message ?? b.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const cq = (companyId?: number): string => (companyId != null ? `?companyId=${companyId}` : '');

export async function listPlaybooks(companyId?: number): Promise<PlaybookDefinition[]> {
  const r = await req<{ items: PlaybookDefinition[] }>(`${URL}${cq(companyId)}`);
  return r.items;
}

export async function getPlaybook(id: number): Promise<PlaybookDefinition> {
  return req<PlaybookDefinition>(`${URL}/${id}`);
}

export async function createPlaybook(input: CreatePlaybookInput): Promise<PlaybookDefinition> {
  return req<PlaybookDefinition>(URL, json('POST', input));
}

export async function updatePlaybook(id: number, fields: UpdatePlaybookFields): Promise<PlaybookDefinition> {
  return req<PlaybookDefinition>(`${URL}/${id}`, json('PUT', fields));
}

export async function deletePlaybook(id: number): Promise<void> {
  await req(`${URL}/${id}`, { method: 'DELETE' });
}

/** Run-Historie einer Definition (FR19). */
export async function listRuns(id: number, limit?: number): Promise<PlaybookRun[]> {
  const q = limit != null ? `?limit=${limit}` : '';
  const r = await req<{ items: PlaybookRun[] }>(`${URL}/${id}/runs${q}`);
  return r.items;
}

/** Laufende/pausierte Runs einer Firma (Live-Status, FR21). */
export async function listLiveRuns(companyId?: number): Promise<PlaybookRun[]> {
  const r = await req<{ items: PlaybookRun[] }>(`${URL}/runs/live${cq(companyId)}`);
  return r.items;
}

/** Ein Run inkl. seines Event-Streams (Observability). */
export async function getRun(runId: string): Promise<RunWithEvents> {
  return req<RunWithEvents>(`${URL}/runs/${encodeURIComponent(runId)}`);
}

/** Dry-Run einer Definition starten (FR20). Keine Außenwirkung. */
export async function dryRunPlaybook(id: number, context?: Record<string, unknown>): Promise<RunWithEvents> {
  return req<RunWithEvents>(`${URL}/${id}/dry-run`, json('POST', { context: context ?? {} }));
}
